import { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { HeroTitle } from "@/components/abilities/editorial";

/** Masthead: the title in the house display grammar, a one-line subtitle, and
 *  whatever the tab can act on, kept to the right. */
export default function Masthead({
  eyebrow,
  title,
  subtitle,
  actions,
  lead,
  lede,
  headWrapperClass = "lib-head",
  mainWrapperClass = "lib-head__main",
  titleWrapperClass = "lib-head__title",
  subtitleWrapperClass = "lib-head__sub",
  actionsWrapperClass = "lib-head__side",
}: {
  eyebrow?: string | ReactNode;
  title?: string | ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  lead?: ReactNode;
  lede?: ReactNode;
  headWrapperClass?: string;
  mainWrapperClass?: string;
  titleWrapperClass?: string;
  subtitleWrapperClass?: string;
  actionsWrapperClass?: string;
}) {
  // A paragraph everywhere it is prose — which is every caller but one: a
  // caller with a `lead` (an identity mark beside the title) can also grow
  // block content inside its subtitle (e.g. an editable field that swaps in a
  // textarea), and a <p> auto-closes around block content, which would move
  // that content out of the subtitle and jump the layout. Callers without
  // `lead` keep the plain <p>.
  const SubtitleTag = lead ? "div" : "p";
  return (
    <div
      className={cn(
        "flex items-start gap-4 max-sm:flex-wrap max-sm:gap-4",
        headWrapperClass
      )}
    >
      <div
        className={cn(
          "flex-1 min-w-0 flex items-center max-sm:gap-3 gap-4.5",
          mainWrapperClass
        )}
      >
        {lead}

        <div className="flex-1 min-w-0 flex flex-col gap-y-2">
          {eyebrow ? (
            typeof eyebrow === "string" ? (
              <p className="font-mono text-2.5 leading-normal tracking-[0.12em] uppercase text-fg-subtle min-w-0 [overflow-wrap:anywhere]">
                {eyebrow}
              </p>
            ) : (
              eyebrow
            )
          ) : null}
          {title ? (
            <HeroTitle
              name={title}
              className={cn(
                "font-serif font-normal text-11 leading-[1.05] tracking-[-0.01em] text-fg-default [overflow-wrap:anywhere] [&_em]:italic max-sm:text-9",
                titleWrapperClass
              )}
            />
          ) : null}
          {subtitle ? (
            <SubtitleTag
              className={cn("text-3.5 text-fg-subtle", subtitleWrapperClass)}
            >
              {subtitle}
            </SubtitleTag>
          ) : null}
          {lede}
        </div>
      </div>

      {actions && (
        <div
          className={cn(
            "flex items-center gap-2 mt-1.5 shrink-0 max-sm:w-full max-sm:mt-0.5 max-sm:[&>button]:flex-1 max-sm:[&>a]:flex-1",
            actionsWrapperClass
          )}
        >
          {actions}
        </div>
      )}
    </div>
  );
}
